import type { Product, Category, SubCategory, StockStat, DigitalCode, InventoryMovement } from '../../types';
import { OrderStatus, DeliveryMethod } from '../../types';
import { translateError } from '../../utils/helpers';
import { supabase } from '../supabaseClient';
import { uploadFile, getFullImageUrl } from '../storage/publicAssets';
import { mapProductResponse } from '../api/mappers';
import { deliveryService } from '../deliveryService';

import { devError } from '@/utils/devLog';
const PRODUCT_SELECT_FULL =
    '*, sub_category:sub_categories(id, nom, categorie_id, categorie:categories(nom))';

/** Cache client pour éviter des requêtes dupliquées (ex. favoris header + carrousel landing). */
const PRODUCTS_BY_IDS_TTL_MS = 60_000;
const productsByIdsCache = new Map<string, { products: Product[]; expiresAt: number }>();
const productsByIdsInflight = new Map<string, Promise<Product[]>>();

function normalizeProductIdsKey(ids: number[]): string {
    const uniq = [...new Set(ids.filter((id) => Number.isFinite(id)))].sort((a, b) => a - b);
    return uniq.join(',');
}

async function getProductById(id: number): Promise<Product> {
    const { data: product, error } = await supabase.from('products').select('*, sub_category:sub_categories(id, nom, categorie_id, categorie:categories(nom))').eq('id', id).single();
    if (error) throw new Error(translateError(error));
    return mapProductResponse(product);
}

export const catalogMethods = {
getProducts: async (): Promise<Product[]> => {
    const { data, error } = await supabase.from('products').select(PRODUCT_SELECT_FULL).order('id', { ascending: false });
    if (error) throw new Error(translateError(error));
    return data.map(mapProductResponse);
},

getProductById,

/**
 * Produits par IDs — même shape que getProducts (cartes, favoris), pas tout le catalogue.
 * Cache mémoire court + fusion des requêtes en cours pour la même liste d’ids.
 */
getProductsByIds: async (ids: number[]): Promise<Product[]> => {
    const key = normalizeProductIdsKey(ids);
    if (!key) return [];

    const now = Date.now();
    const cached = productsByIdsCache.get(key);
    if (cached && cached.expiresAt > now) {
        return cached.products;
    }

    const inflight = productsByIdsInflight.get(key);
    if (inflight) return inflight;

    const uniqueIds = key.split(',').map(Number);
    const promise = (async () => {
        try {
            const { data, error } = await supabase
                .from('products')
                .select(PRODUCT_SELECT_FULL)
                .in('id', uniqueIds)
                .order('id', { ascending: false });
            if (error) throw new Error(translateError(error));
            const products = (data || []).map(mapProductResponse);
            productsByIdsCache.set(key, {
                products,
                expiresAt: Date.now() + PRODUCTS_BY_IDS_TTL_MS,
            });
            return products;
        } finally {
            productsByIdsInflight.delete(key);
        }
    })();

    productsByIdsInflight.set(key, promise);
    return promise;
},

getPhysicalProducts: async (): Promise<Product[]> => {
    const { data, error } = await supabase.from('products').select('*, sub_category:sub_categories(id, nom, categorie_id, categorie:categories(nom))').in('delivery_method', [DeliveryMethod.LIVRAISON_DOMICILE, DeliveryMethod.TRAITEMENT_LIEN]);
    if (error) throw new Error(translateError(error));
    return data.map(mapProductResponse);
},

addProduct: async (data: any): Promise<Product> => {
    let imageUrl = data.image_url;
    let logoUrl = data.logo_url;
    if (data.image_file) imageUrl = await uploadFile('public_pic', data.image_file);
    if (data.logo_file) logoUrl = await uploadFile('public_pic', data.logo_file);

    const payload = { ...data, image_url: imageUrl, logo_url: logoUrl };
    delete payload.id;
    delete payload.sub_category;
    delete payload.categorie_id;
    delete payload.categorie_nom;
    delete payload.sub_category_nom;
    delete payload.image_file;
    delete payload.logo_file;
    delete payload.other_images_files;

    const { data: product, error } = await supabase.from('products').insert(payload).select().single();
    if (error) throw new Error(translateError(error));
    return getProductById(product.id);
},

updateProduct: async (data: any): Promise<Product> => {
    let imageUrl = data.image_url;
    let logoUrl = data.logo_url;
    if (data.image_file) imageUrl = await uploadFile('public_pic', data.image_file);
    if (data.logo_file) logoUrl = await uploadFile('public_pic', data.logo_file);

    const payload = { ...data, image_url: imageUrl, logo_url: logoUrl };
    const productId = payload.id;
    delete payload.id;
    delete payload.sub_category;
    delete payload.categorie_id;
    delete payload.categorie_nom;
    delete payload.sub_category_nom;
    delete payload.image_file;
    delete payload.logo_file;
    delete payload.other_images_files;

    const { error } = await supabase.from('products').update(payload).eq('id', productId);
    if (error) throw new Error(translateError(error));
    return getProductById(productId);
},

deleteProduct: async (id: number) => { await supabase.from('products').delete().eq('id', id); },

checkProductDependencies: async (id: number) => {
    const { count: codes } = await supabase.from('digital_codes').select('*', { count: 'exact', head: true }).eq('product_id', id);
    return { total: codes || 0 };
},

forceDeleteProduct: async (id: number) => {
    await supabase.from('digital_codes').delete().eq('product_id', id);
    await supabase.from('inventory_movements').delete().eq('product_id', id);
    await supabase.from('reviews').delete().eq('product_id', id);
    await supabase.from('favorites').delete().eq('product_id', id);
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) throw new Error(translateError(error));
},

getCategories: async (): Promise<Category[]> => {
    const { data, error } = await supabase.from('categories').select('*').order('nom');
    if (error) throw new Error(translateError(error));
    return data.map((c: any) => ({ ...c, image_url: getFullImageUrl(c.image_url) }));
},

addCategory: async (data: any) => {
    let url = '';
    if (data.image_file) url = await uploadFile('public_pic', data.image_file);
    await supabase.from('categories').insert({ nom: data.nom, description: data.description, image_url: url });
},

updateCategory: async (data: any) => {
    let url = data.image_url;
    if (data.image_file) url = await uploadFile('public_pic', data.image_file);
    await supabase.from('categories').update({ nom: data.nom, description: data.description, image_url: url }).eq('id', data.id);
},

deleteCategory: async (id: string) => { await supabase.from('categories').delete().eq('id', id); },

getSubCategories: async (): Promise<SubCategory[]> => {
    const { data, error } = await supabase.from('sub_categories').select('*').order('nom');
    if (error) throw new Error(translateError(error));
    return data.map((s: any) => ({ ...s, image_url: getFullImageUrl(s.image_url) }));
},

addSubCategory: async (data: any) => {
    let url = '';
    if (data.image_file) url = await uploadFile('public_pic', data.image_file);
    await supabase.from('sub_categories').insert({ nom: data.nom, description: data.description, categorie_id: data.categorie_id, image_url: url });
},

updateSubCategory: async (data: any) => {
    let url = data.image_url;
    if (data.image_file) url = await uploadFile('public_pic', data.image_file);
    await supabase.from('sub_categories').update({ nom: data.nom, description: data.description, image_url: url }).eq('id', data.id);
},

deleteSubCategory: async (id: string) => { await supabase.from('sub_categories').delete().eq('id', id); },

getStockStats: async (): Promise<StockStat[]> => {
    const { data } = await supabase.rpc('get_stock_stats');
    return data || [];
},

importCodes: async (productId: number, codes: string[], serials: string[], expiryDates?: Date[]): Promise<number> => {
    const timestamp = Date.now();
    const rows = codes.map((code, idx) => ({
        product_id: productId,
        reference: `IMP-${timestamp}-${idx}`,
        code_encrypted: code,
        code_hash: code,
        serial_number: serials[idx],
        status: 'AVAILABLE',
        expiration_date: expiryDates?.[idx] ? expiryDates[idx].toISOString() : null
    }));
    const { data, error } = await supabase.from('digital_codes').insert(rows).select();
    if (error) throw new Error(translateError(error));
    await deliveryService.syncProductStatus(productId);
    return data.length;
},

adjustPhysicalStock: async (productId: number, quantity: number, reason: string, type: 'IN' | 'OUT') => {
    const { data: product } = await supabase.from('products').select('stock_quantity').eq('id', productId).single();
    const currentStock = product?.stock_quantity || 0;
    const newStock = type === 'IN' ? currentStock + quantity : Math.max(0, currentStock - quantity);
    await supabase.from('products').update({ stock_quantity: newStock }).eq('id', productId);
    await supabase.from('inventory_movements').insert({ product_id: productId, movement_type: type, quantity: quantity, reason: reason });
    await deliveryService.syncProductStatus(productId);
},

getInventoryMovements: async (productId: number): Promise<InventoryMovement[]> => {
    const { data } = await supabase.from('inventory_movements').select('*, user:profiles(nom, prénom)').eq('product_id', productId).order('created_at', { ascending: false });
    return (data as any) || [];
},

getMyCodes: async (userId: string): Promise<DigitalCode[]> => {
    const { data } = await supabase.rpc('get_user_codes', { p_user_id: userId });
    
    // Récupérer les commandes pour trouver l'order_id de chaque code
    // et aussi récupérer les codes qui sont dans les commandes mais qui ont buyer_id null
    // IMPORTANT: Ne récupérer que les codes des commandes terminées (statut = 'completed')
    // pour éviter le double affichage sur mobile
    const { data: orders } = await supabase.from('orders').select('id, items, date, statut').eq('user_id', userId);
    const orderMap = new Map<number, number>(); // code_id -> order_id
    const codesFromOrders = new Map<number, any>(); // code_id -> code info from order
    
    if (orders) {
        orders.forEach(order => {
            // Ne traiter que les commandes terminées (statut = 'completed')
            // Les codes instantanés ne doivent être visibles qu'après validation admin
            if (order.statut !== OrderStatus.COMPLETED) {
                return;
            }
            
            order.items.forEach((item: any) => {
                if (item.delivery_metadata?.codes) {
                    item.delivery_metadata.codes.forEach((codeInfo: any) => {
                        if (codeInfo.id) {
                            orderMap.set(codeInfo.id, order.id);
                            // Stocker les infos du code depuis la commande
                            codesFromOrders.set(codeInfo.id, {
                                id: codeInfo.id,
                                reference: codeInfo.ref || codeInfo.reference,
                                order_id: order.id,
                                sold_at: order.date,
                                product_id: item.produit_id,
                                nom_produit: item.nom_produit
                            });
                        }
                    });
                }
                // Gérer aussi l'ancien format avec code_id direct
                if (item.delivery_metadata?.code_id) {
                    const codeId = item.delivery_metadata.code_id;
                    orderMap.set(codeId, order.id);
                    codesFromOrders.set(codeId, {
                        id: codeId,
                        reference: item.delivery_metadata.reference,
                        order_id: order.id,
                        sold_at: order.date,
                        product_id: item.produit_id,
                        nom_produit: item.nom_produit
                    });
                }
            });
        });
    }
    
    // Récupérer les codes qui sont dans les commandes mais pas dans get_user_codes
    // (cas où buyer_id est null)
    const codesFromOrdersIds = Array.from(codesFromOrders.keys());
    const existingCodeIds = new Set((data || []).map((c: any) => c.id));
    const missingCodeIds = codesFromOrdersIds.filter(id => !existingCodeIds.has(id));
    
    let additionalCodes: any[] = [];
    if (missingCodeIds.length > 0) {
        // Récupérer les infos des codes depuis digital_codes
        const { data: missingCodes } = await supabase
            .from('digital_codes')
            .select('id, product_id, reference, serial_number, status, expiration_date, created_at, sold_at, buyer_id')
            .in('id', missingCodeIds);
        
        if (missingCodes) {
            // Récupérer les produits pour ces codes
            const productIds = [...new Set(missingCodes.map((c: any) => c.product_id))];
            const { data: products } = await supabase
                .from('products')
                .select('id, nom, image_url, logo_url, valeur')
                .in('id', productIds);
            
            const productMap = new Map((products || []).map((p: any) => [p.id, p]));
            
            additionalCodes = missingCodes.map((code: any) => {
                const orderInfo = codesFromOrders.get(code.id);
                const product = productMap.get(code.product_id);
                return {
                    id: code.id,
                    product_id: code.product_id,
                    order_id: orderInfo?.order_id,
                    order_item_nom_produit: orderInfo?.nom_produit,
                    reference: code.reference,
                    serial_number: code.serial_number,
                    status: code.status.toLowerCase() as 'available' | 'sold' | 'expired',
                    expiration_date: code.expiration_date,
                    created_at: code.created_at,
                    sold_at: orderInfo?.sold_at || code.sold_at,
                    buyer_id: code.buyer_id,
                    product: product ? mapProductResponse(product) : undefined
                };
            });
        }
    }
    
    // Combiner les codes de get_user_codes et ceux des commandes.
    // order_item_nom_produit = nom de la ligne de commande à laquelle le code a été assigné (évite d'afficher le mauvais produit si assignation incorrecte).
    const allCodes = [
        ...(data || []).map((c: any) => {
            const orderInfo = codesFromOrders.get(c.id);
            return {
                ...c,
                order_id: orderMap.get(c.id),
                product: c.product ? mapProductResponse(c.product) : undefined,
                order_item_nom_produit: orderInfo?.nom_produit
            };
        }),
        ...additionalCodes
    ];
    
    // Supprimer les doublons (au cas où un code serait dans les deux listes)
    const uniqueCodes = Array.from(
        new Map(allCodes.map(c => [c.id, c])).values()
    );
    
    return uniqueCodes.sort((a, b) => {
        const dateA = new Date(a.sold_at || a.created_at).getTime();
        const dateB = new Date(b.sold_at || b.created_at).getTime();
        return dateB - dateA;
    });
},

revealCode: async (codeId: number): Promise<string> => {
    try {
        // Essayer d'abord avec reveal_my_code (plus stricte, vérifie buyer_id)
        const { data: myCodeData, error: myCodeError } = await supabase.rpc('reveal_my_code', { p_code_id: codeId });
        if (!myCodeError && myCodeData && myCodeData.trim() !== '') {
            return myCodeData;
        }
        
        // Si reveal_my_code échoue, essayer avec reveal_digital_code (plus permissive, permet aussi aux admins)
        // Cela gère aussi le cas où buyer_id est null mais le code est dans une commande
        const { data, error } = await supabase.rpc('reveal_digital_code', { p_code_id: codeId });
        if (error) {
            devError('RPC reveal_digital_code error:', error);
            throw new Error(error.message || "Code introuvable ou accès refusé");
        }
        if (!data || data.trim() === '') {
            throw new Error("Le code est vide ou introuvable");
        }
        return data;
    } catch (err: any) {
        devError('revealCode error:', err);
        // Message d'erreur plus informatif
        const errorMsg = err.message || "Erreur de sécurité : Impossible de récupérer le code secret.";
        throw new Error(errorMsg);
    }
},

getDigitalCodes: async (filters: { productId?: number; status?: string; search?: string; page: number; perPage: number }): Promise<{ data: any[]; total: number }> => {
    const page = Math.max(1, filters.page || 1);
    const perPage = Math.max(1, Math.min(200, filters.perPage || 20));
    const from = (page - 1) * perPage;
    const to = from + perPage - 1;

    let query = supabase
        .from('digital_codes')
        .select('id, product_id, reference, serial_number, code_encrypted, code_hash, status, expiration_date, buyer_id, sold_at, created_at', { count: 'exact' });

    if (filters.productId) query = query.eq('product_id', filters.productId);
    if (filters.status && filters.status !== 'ALL') {
        const s = String(filters.status);
        // Support historique: statuts en minuscules dans certains enregistrements.
        // Support UX: "SOLD_OR_USED" = vendu + utilisé.
        if (s === 'SOLD_OR_USED') {
            query = query.in('status', ['SOLD', 'USED', 'sold', 'used']);
        } else {
            query = query.in('status', [s, s.toLowerCase()]);
        }
    }
    if (filters.search && filters.search.trim()) {
        const q = filters.search.trim();
        query = query.or(`reference.ilike.%${q}%,serial_number.ilike.%${q}%`);
    }

    const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

    if (error) throw new Error(translateError(error));
    return { data: (data as any[]) || [], total: count || 0 };
},

updateDigitalCodeStatus: async (codeId: number, newStatus: 'AVAILABLE' | 'SOLD' | 'EXPIRED' | 'USED'): Promise<void> => {
    const { error } = await supabase.from('digital_codes').update({ status: newStatus }).eq('id', codeId);
    if (error) throw new Error(translateError(error));
},
};
